import {
  DevErrorJson,
  useDevServerStore,
} from "@/store/zustand/devServerStore";
import { STORYBOARD_HYDRATION_STATUS } from "@/utils/constants";
import { useSelector } from "react-redux";

export const useErrorsForStoryboard = (
  storyboardId: string
): {
  anyErrorForThisStoryboard: boolean;
  internalErrorForThisStoryboard: any;
  externalErrorForThisStoryboard: any;
} => {
  const storyboardIdToError = useDevServerStore(
    (store) => store.storyboardIdToError
  );
  const externalErrorForThisStoryboard = storyboardIdToError?.[storyboardId];

  const storyboardIdToInternalError = useSelector(
    (state: any) => state.iframe.storyboardIdToInternalError
  );
  const internalErrorForThisStoryboard =
    storyboardIdToInternalError?.[storyboardId];

  const internalIsError =
    internalErrorForThisStoryboard?.status ===
      STORYBOARD_HYDRATION_STATUS.ERROR ||
    internalErrorForThisStoryboard?.status ===
      STORYBOARD_HYDRATION_STATUS.OTHER_ERROR;

  // Put into the expected format
  const anyErrorForThisStoryboard: boolean = Boolean(
    externalErrorForThisStoryboard || internalIsError
  );

  return {
    anyErrorForThisStoryboard,
    internalErrorForThisStoryboard,
    externalErrorForThisStoryboard,
  };
};

export const useJoinedErrors = (): DevErrorJson[] => {
  const storyboardIdToError = useDevServerStore(
    (store) => store.storyboardIdToError
  );

  const storyboardIdToInternalError: { [key: string]: any } = useSelector(
    (state: any) => state.iframe.storyboardIdToInternalError
  );

  const terminalErrors = useDevServerStore((state) => state.terminalErrors);

  // Group by equal message, filename and stack
  const groupedErrors = Object.entries(storyboardIdToError)
    // @ts-expect-error TS2769
    .concat(terminalErrors.map((error) => [null, error]))
    .reduce((acc, [storyboardId, error]) => {
      if (!error) {
        return acc;
      }

      if (
        acc[error.message] &&
        acc[error.message].filename === error.filename &&
        acc[error.message].stack === error.stack
      ) {
        acc[error.message].count++;
        if (storyboardId) {
          acc[error.message].storyboards.push(storyboardId);
        }
      } else {
        acc[error.message] = {
          ...error,
          count: 1,
          storyboards: storyboardId ? [storyboardId] : [],
        };
      }
      return acc;
    }, {});

  // Also group the internal errors (they have a descr, label and body which map to the external errors)
  const joinedErrors: { [key: string]: DevErrorJson } = Object.entries(
    storyboardIdToInternalError
  ).reduce((acc, [storyboardId, error]) => {
    const isError =
      error?.status === STORYBOARD_HYDRATION_STATUS.ERROR ||
      error?.status === STORYBOARD_HYDRATION_STATUS.OTHER_ERROR;

    if (!isError) {
      return acc;
    }

    if (
      acc[error.descr] &&
      acc[error.descr].label === error.label &&
      acc[error.descr].body === error.body
    ) {
      acc[error.descr].count++;
      acc[error.descr].storyboards.push(storyboardId);
    } else {
      acc[error.descr] = {
        message: error.descr,
        filename: error.label,
        stack: error.body,
        count: 1,
        storyboards: [storyboardId],
      };
    }
    return acc;
  }, groupedErrors);

  return Object.values(joinedErrors);
};

export const getAiMessageFromErrors = (errors: DevErrorJson[]) => {
  return {
    role: "user",
    content: "\n<dev-errors>\n" + JSON.stringify(errors) + "\n</dev-errors>\n",
  };
};

// Reverse of getAiMessageFromErrors
export const getErrorsFromAIMessageContent = (content: string) => {
  const startTag = "<dev-errors>";
  const endTag = "</dev-errors>";

  const jsonContent = content
    .replace(new RegExp(`${startTag}\\n`), "")
    .replace(new RegExp(`\\n${endTag}`), "");

  try {
    return JSON.parse(jsonContent) as DevErrorJson[];
  } catch (e) {
    return [];
  }
};
